import { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { heroSlides } from "@/mocks/media";

export default function HeroCarousel() {
  const [index, setIndex] = useState(0);
  const touchStartX = useRef<number | null>(null);
  const total = heroSlides.length;

  const goTo = useCallback(
    (next: number) => {
      setIndex(((next % total) + total) % total);
    },
    [total],
  );

  useEffect(() => {
    const timer = window.setInterval(() => {
      setIndex((prev) => (prev + 1) % total);
    }, 6000);
    return () => window.clearInterval(timer);
  }, [total]);

  const active = heroSlides[index];

  const handleTouchStart = (event: React.TouchEvent) => {
    touchStartX.current = event.touches[0].clientX;
  };

  const handleTouchEnd = (event: React.TouchEvent) => {
    if (touchStartX.current === null) return;
    const delta = event.changedTouches[0].clientX - touchStartX.current;
    if (Math.abs(delta) > 50) {
      goTo(delta < 0 ? index + 1 : index - 1);
    }
    touchStartX.current = null;
  };

  return (
    <section
      className="relative h-[440px] w-full overflow-hidden md:h-[600px]"
      onTouchStart={handleTouchStart}
      onTouchEnd={handleTouchEnd}
      aria-label="焦点影片轮播"
    >
      {heroSlides.map((slide, i) => (
        <div
          key={slide.id}
          className={`absolute inset-0 transition-opacity duration-700 ${
            i === index ? "opacity-100" : "opacity-0"
          }`}
        >
          <img
            src={slide.image}
            alt={`${slide.title} 焦点图`}
            title={`${slide.title} - 麻豆精品`}
            className="h-full w-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-foreground-950/90 via-foreground-950/60 to-foreground-950/20"></div>
        </div>
      ))}

      <div className="relative z-10 mx-auto flex h-full w-full max-w-[1280px] flex-col justify-end px-4 pb-14 md:px-6 md:pb-20">
        <div key={active.id} className="max-w-2xl animate-[fadeIn_0.6s_ease]">
          <span className="inline-flex items-center gap-2 rounded-full bg-accent-500 px-3 py-1 text-xs font-bold text-foreground-950">
            <i className="ri-fire-fill" aria-hidden="true"></i>
            {active.kicker}
          </span>
          <h1 className="mt-4 font-heading text-3xl font-extrabold leading-tight text-background-50 md:text-5xl">
            {active.title}
          </h1>
          <p className="mt-3 max-w-xl text-sm leading-relaxed text-background-100/90 md:text-base">
            {active.desc}
          </p>
          <div className="mt-6 flex flex-wrap items-center gap-3">
            <Link
              to={`/detail/${active.targetId}`}
              className="flex cursor-pointer items-center gap-2 whitespace-nowrap rounded-full bg-accent-500 px-5 py-2.5 text-sm font-semibold text-foreground-950 transition-colors hover:bg-accent-600"
            >
              <i className="ri-play-fill" aria-hidden="true"></i>
              查看详情
            </Link>
            <Link
              to="/category/movie"
              className="flex cursor-pointer items-center gap-2 whitespace-nowrap rounded-full border border-background-50/40 bg-background-50/10 px-5 py-2.5 text-sm font-semibold text-background-50 backdrop-blur transition-colors hover:bg-background-50/20"
            >
              浏览全部影片
            </Link>
          </div>
        </div>
      </div>

      <div className="absolute bottom-5 right-4 z-10 flex items-center gap-2 md:right-8 md:bottom-8">
        <button
          type="button"
          onClick={() => goTo(index - 1)}
          aria-label="上一个"
          className="flex h-9 w-9 cursor-pointer items-center justify-center rounded-full border border-background-50/40 bg-foreground-950/30 text-background-50 backdrop-blur transition-colors hover:bg-foreground-950/50 md:h-10 md:w-10"
        >
          <i className="ri-arrow-left-s-line text-lg" aria-hidden="true"></i>
        </button>
        <button
          type="button"
          onClick={() => goTo(index + 1)}
          aria-label="下一个"
          className="flex h-9 w-9 cursor-pointer items-center justify-center rounded-full border border-background-50/40 bg-foreground-950/30 text-background-50 backdrop-blur transition-colors hover:bg-foreground-950/50 md:h-10 md:w-10"
        >
          <i className="ri-arrow-right-s-line text-lg" aria-hidden="true"></i>
        </button>
      </div>

      <div className="absolute bottom-5 left-1/2 z-10 flex -translate-x-1/2 items-center gap-2 md:bottom-8 md:left-6 md:translate-x-0">
        {heroSlides.map((slide, i) => (
          <button
            key={slide.id}
            type="button"
            onClick={() => goTo(i)}
            aria-label={`切换到第 ${i + 1} 张`}
            className={`h-1.5 cursor-pointer rounded-full transition-all ${
              i === index ? "w-8 bg-accent-500" : "w-4 bg-background-50/50"
            }`}
          ></button>
        ))}
      </div>
    </section>
  );
}